import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminFilterBar from '../../components/admin/AdminFilterBar';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminMetricCard from '../../components/admin/AdminMetricCard';
import AdminStatusBadge from '../../components/admin/AdminStatusBadge';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import { defaultUtcRangeDays } from '../../admin/adminHubUtils';
import { copyTextToClipboard, exportAdminTableAsCsv, exportAdminTableAsJson, normalizeAdminExportRows } from '../../admin/adminExportUtils';
import { filterAdminTableRows, nextSortState, sortAdminTableRows } from '../../admin/adminTableClient';

function n(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function heurTone(h) {
  if (h === 'healthy' || h === 'normal') return 'ok';
  if (h === 'watch' || h === 'notice' || h === 'elevated') return 'warn';
  if (h === 'degraded' || h === 'heavy') return 'danger';
  return 'neutral';
}

/**
 * @param {{
 *   columns: Array<{ key: string, header: string }>,
 *   rows: Array<Record<string, unknown>>,
 *   exportPrefix: string,
 *   filterKeys: string[],
 * }} props
 */
function OpsFilterableTable({ columns, rows, exportPrefix, filterKeys }) {
  const { t } = useLanguage();
  const columnKeys = useMemo(() => columns.map((c) => c.key), [columns]);
  const [search, setSearch] = useState('');
  const [fCol, setFCol] = useState(() => Object.fromEntries(filterKeys.map((k) => [k, ''])));
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    setFCol(Object.fromEntries(filterKeys.map((k) => [k, ''])));
    setSearch('');
    setSortKey(null);
    setSortDir(null);
  }, [rows, filterKeys]);

  const colFilters = useMemo(() => {
    const o = {};
    filterKeys.forEach((k) => {
      o[k] = fCol[k] || '';
    });
    return o;
  }, [fCol, filterKeys]);

  const filtered = useMemo(
    () => sortAdminTableRows(filterAdminTableRows(rows, columnKeys, search, colFilters), sortKey, sortDir),
    [rows, columnKeys, search, colFilters, sortKey, sortDir]
  );

  const onSort = useCallback(
    (key) => {
      const nxt = nextSortState(sortKey, sortDir, key);
      setSortKey(nxt.key);
      setSortDir(nxt.dir);
    },
    [sortKey, sortDir]
  );

  const toolbarFilters = useMemo(
    () =>
      filterKeys.map((k) => ({
        id: k,
        label: columns.find((c) => c.key === k)?.header || k,
        value: fCol[k] || '',
        onChange: (v) => setFCol((prev) => ({ ...prev, [k]: v })),
      })),
    [filterKeys, columns, fCol]
  );

  const hasFilter = !!(search.trim() || filterKeys.some((k) => (fCol[k] || '').trim()));

  return (
    <>
      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        filteredCount={filtered.length}
        totalCount={rows.length}
        compact={compact}
        onCompactChange={setCompact}
        onExportCsv={() => exportAdminTableAsCsv(filtered, columnKeys, exportPrefix)}
        onExportJson={() => exportAdminTableAsJson(filtered, columnKeys, exportPrefix)}
        onCopyFilteredJson={() => copyTextToClipboard(JSON.stringify(normalizeAdminExportRows(filtered, columnKeys)))}
        filters={toolbarFilters}
      />
      <AdminDataTable
        columns={columns}
        rows={filtered}
        emptyMessage={hasFilter ? t('adminExport.noResults') : t('adminKit.tableEmpty')}
        emptyVariant={hasFilter ? 'noResults' : 'default'}
        compact={compact}
        sortable
        sortKey={sortKey}
        sortDir={sortDir}
        onSortChange={onSort}
        copyRowKeys={columnKeys}
      />
    </>
  );
}

export default function AdminOperationsAiVisibilityPage() {
  const { t } = useLanguage();
  const def = defaultUtcRangeDays(14);
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [officeId, setOfficeId] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(false);
    try {
      const q = new URLSearchParams({ from: from.trim(), to: to.trim() });
      if (officeId.trim()) q.set('office_id', officeId.trim());
      const res = await fetch(`${buildApiUrl(API_ENDPOINTS.ADMIN_OPERATIONAL_INSIGHTS)}?${q}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('bad');
      setData(await res.json());
    } catch {
      setErr(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const health = data?.health;
  const totals = health?.totals || {};
  const heur = health?.heuristics || {};

  const topCols = useMemo(
    () => [
      { key: 'source_project', header: t('adminKit.colSourceProject') },
      { key: 'usage_type', header: t('adminKit.colUsageType') },
      { key: 'usage_subtype', header: t('adminKit.colUsageSubtype') },
      { key: 'event_count', header: t('adminKit.colEvents') },
      { key: 'usage_units_sum', header: t('adminKit.colUnits') },
    ],
    [t]
  );

  const guestCols = useMemo(
    () => [
      { key: 'guest_bucket', header: t('adminKit.colGuestBucket') },
      { key: 'event_count', header: t('adminKit.colEvents') },
      { key: 'usage_units_sum', header: t('adminKit.colUnits') },
    ],
    [t]
  );

  const officeCols = useMemo(
    () => [
      { key: 'office_id', header: t('adminKit.colOfficeId') },
      { key: 'event_count', header: t('adminKit.colEvents') },
      { key: 'usage_units_sum', header: t('adminKit.colUnits') },
    ],
    [t]
  );

  const topRows = useMemo(() => (Array.isArray(data?.topAiUsageByType) ? data.topAiUsageByType : []).slice(0, 40), [data]);
  const guestRows = useMemo(() => (Array.isArray(data?.guestPressure) ? data.guestPressure : []).slice(0, 25), [data]);
  const officeRows = useMemo(
    () => (Array.isArray(data?.officeAiConsumptionTop) ? data.officeAiConsumptionTop : []).slice(0, 25),
    [data]
  );

  const topFilterKeys = useMemo(() => ['usage_type', 'source_project'], []);
  const guestFilterKeys = useMemo(() => ['guest_bucket'], []);
  const officeFilterKeys = useMemo(() => ['office_id'], []);

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navOperations'), to: '/admin/operations/ai-visibility' },
      { label: t('adminHub.navAiVisibility') },
    ],
    [t]
  );

  return (
    <AdminHubPageFrame
      titleKey="adminHub.aiVisibilityTitle"
      descKey="adminHub.aiVisibilityDesc"
      breadcrumbItems={crumbs}
    >
      <section className="admin-section-card">
        <AdminFilterBar>
          <label className="admin-hub-kit-toolbar__label">
            {t('adminHub.dateFrom')}
            <input className="admin-hub-kit-toolbar__input" value={from} onChange={(e) => setFrom(e.target.value)} type="text" inputMode="numeric" />
          </label>
          <label className="admin-hub-kit-toolbar__label">
            {t('adminHub.dateTo')}
            <input className="admin-hub-kit-toolbar__input" value={to} onChange={(e) => setTo(e.target.value)} type="text" inputMode="numeric" />
          </label>
          <label className="admin-hub-kit-toolbar__label">
            {t('adminHub.officeIdOptional')}
            <input className="admin-hub-kit-toolbar__input" value={officeId} onChange={(e) => setOfficeId(e.target.value)} />
          </label>
          <button type="button" className="admin-hub-btn" onClick={load} disabled={loading}>
            {loading ? t('adminPage.loading') : t('adminHub.loadAction')}
          </button>
        </AdminFilterBar>
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : null}
        {data ? (
          <div className="admin-hub-kit-stack">
            <AdminSectionHeader title={t('adminKit.opsWindowTitle')} subtitle={t('adminKit.opsWindowSub')} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard label={t('adminKit.colDateFrom')} value={data.window?.from || '—'} />
              <AdminMetricCard label={t('adminKit.colDateTo')} value={data.window?.to || '—'} />
            </div>

            <AdminSectionHeader title={t('adminKit.opsHealthTotalsTitle')} level={3} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard label={t('adminKit.metricEventCount')} value={n(totals.event_count)} />
              <AdminMetricCard label={t('adminKit.metricUsageUnits')} value={n(totals.usage_units_sum)} />
              <AdminMetricCard label={t('adminKit.metricGuestEvents')} value={n(totals.guest_events)} />
              <AdminMetricCard label={t('adminKit.metricGuestUnits')} value={n(totals.guest_units)} />
            </div>

            <AdminSectionHeader title={t('adminKit.opsHeuristicsTitle')} level={3} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard
                label={t('adminKit.heurAiHealth')}
                value={<AdminStatusBadge tone={heurTone(heur.ai_usage_health)}>{heur.ai_usage_health || '—'}</AdminStatusBadge>}
              />
              <AdminMetricCard
                label={t('adminKit.heurOcrLoad')}
                value={<AdminStatusBadge tone={heurTone(heur.ocr_load)}>{heur.ocr_load || '—'}</AdminStatusBadge>}
              />
              <AdminMetricCard
                label={t('adminKit.heurLlmPressure')}
                value={<AdminStatusBadge tone={heurTone(heur.llm_heavy_operations)}>{heur.llm_heavy_operations || '—'}</AdminStatusBadge>}
              />
              <AdminMetricCard label={t('adminKit.heurFailedRatio')} value={heur.failed_analysis_ratio ?? '—'} />
            </div>

            {data.officePolicyPreview ? (
              <>
                <AdminSectionHeader title={t('adminKit.officePolicyPreviewTitle')} level={3} />
                <p className="admin-hub-kit-note">{t('adminKit.officePolicyPreviewNote')}</p>
                <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={data.officePolicyPreview} defaultOpen />
              </>
            ) : null}

            <AdminSectionHeader title={t('adminKit.topAiUsageTitle')} level={3} />
            <OpsFilterableTable
              columns={topCols}
              rows={topRows}
              exportPrefix="ops-ai-top-by-type"
              filterKeys={topFilterKeys}
            />

            <AdminSectionHeader title={t('adminKit.guestPressureTitle')} level={3} />
            <OpsFilterableTable columns={guestCols} rows={guestRows} exportPrefix="ops-ai-guest-pressure" filterKeys={guestFilterKeys} />

            <AdminSectionHeader title={t('adminKit.officeConsumptionTitle')} level={3} />
            <OpsFilterableTable columns={officeCols} rows={officeRows} exportPrefix="ops-ai-office-top" filterKeys={officeFilterKeys} />

            <AdminJsonDetails title={t('adminKit.rawBundleJson')} data={data} />
          </div>
        ) : null}
      </section>
    </AdminHubPageFrame>
  );
}
